import { getStepToGroupStepMap, getSubStepMap } from '../../utils/wizardUtils';

import {
  PROGRESS_STEP,
  PROGRESS_STEP_CONFIG,
  PROGRESS_STEP_MULTIPLEX_LISTING_CONFIG,
  PROGRESS_STEP_RELISTING_CONFIG,
  PROGRESS_STEP_RELISTING_TOUR_BOOKING_CONFIG,
  STEP,
} from './constants';

import AcceptZillowApplications from '../../components/WizardModularListing/AcceptZillowApplications';
import AddPhotos from '../../components/WizardModularListing/AddPhotos';
import AllowContactByPhone from '../../components/WizardModularListing/AllowContactByPhone';
import AmenitiesInterior from '../../components/WizardModularListing/AmenitiesInterior';
import AmenitiesProperty from '../../components/WizardModularListing/AmenitiesProperty';
import CostsAndFees from '../../components/WizardModularListing/CostsAndFees';
import DescribeProperty from '../../components/WizardModularListing/DescribeProperty';
import FastPostReview from '../../components/WizardModularListing/FastPostReview';
import FinancialExpectations from '../../components/WizardModularListing/FinancialExpectations';
import HidePropertyAddress from '../../components/WizardModularListing/HidePropertyAddress';
import LeaseDuration from '../../components/WizardModularListing/LeaseDuration';
import LeaseTerms from '../../components/WizardModularListing/LeaseTerms';
import MonthlyRentAndSecurityDeposit from '../../components/WizardModularListing/MonthlyRentAndSecurityDeposit';
import MultiplexListingPublished from '../../components/WizardModularListing/MultiplexListingPublished';
import MultiplexOverview from '../../components/WizardModularListing/MultiplexOverview';
import MultiplexPropertyDetails from '../../components/WizardModularListing/MultiplexPropertyDetails';
import PayAndPublish from '../../components/WizardModularListing/PayAndPublish';
import PetPolicy from '../../components/WizardModularListing/PetPolicy';
import PetPolicyAndPetFee from '../../components/WizardModularListing/PetPolicyAndPetFee';
import PhoneNumber from '../../components/WizardModularListing/PhoneNumber';
import PropertyDescription from '../../components/WizardModularListing/PropertyDescription';
import RelistAdditionalDetails from '../../components/WizardModularListing/RelistAdditionalDetails';
import RelistConfirmDetails from '../../components/WizardModularListing/RelistConfirmDetails';
import RenterPreferences from '../../components/WizardModularListing/RenterPreferences';
import Review from '../../components/WizardModularListing/Review';
import RoomCurrentPets from '../../components/WizardModularListing/RoomCurrentPets';
import RoomHowManyRoommates from '../../components/WizardModularListing/RoomHowManyRoommates';
import RoommateDescription from '../../components/WizardModularListing/RoommateDescription';
import RoomUtilitiesAmount from '../../components/WizardModularListing/RoomUtilitiesAmount';
import RoomWhoIsListingProperty from '../../components/WizardModularListing/RoomWhoIsListingProperty';
import TourSchedulingAvailability from '../../components/WizardModularListing/TourSchedulingSetup/TourSchedulingAvailability';
import TourSchedulingBookingMethod from '../../components/WizardModularListing/TourSchedulingSetup/TourSchedulingBookingMethod';
import TourSchedulingTimeZone from '../../components/WizardModularListing/TourSchedulingSetup/TourSchedulingTimeZone';
import WhenIsPropertyAvailable from '../../components/WizardModularListing/WhenIsPropertyAvailable';
import WhoIsListingProperty from '../../components/WizardModularListing/WhoIsListingProperty';

import type { WizardStepType } from '../../components/Wizard/types';

/**
 * Add ALL of the steps possible that will populate the config array
 * Steps will get dynamically removed within the component that renders the WizardStepController via wizardUtils
 *
 * This is important to set ORIGINAL_CONFIG which keeps dynamically re-adding steps in sync
 */

const configStepGroupPropertyInfo: Array<WizardStepType> = [
  {
    stepKey: STEP.ROOM_WHO_IS_LISTING_PROPERTY,
    // @ts-expect-error
    wizardBodyTemplate: RoomWhoIsListingProperty,
    progressStepKey: PROGRESS_STEP.PROPERTY_INFO,
    hideProgressStepper: false,
  },
  /**
   * We need a unique stepKey, but can reuse the component for wizardBodyTemplate.
   * The changes for room for rent will be made within the component.
   * The form field(s) share the same key(s) that will be saved.
   */
  {
    stepKey: STEP.ROOM_DESCRIPTION,
    // @ts-expect-error
    wizardBodyTemplate: DescribeProperty,
    progressStepKey: PROGRESS_STEP.PROPERTY_INFO,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.PROPERTY_INFO,
    // @ts-expect-error
    wizardBodyTemplate: PropertyDescription,
    progressStepKey: PROGRESS_STEP.PROPERTY_INFO,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.HIDE_PROPERTY_ADDRESS,
    // @ts-expect-error
    wizardBodyTemplate: HidePropertyAddress,
    progressStepKey: PROGRESS_STEP.PROPERTY_INFO,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.ROOM_HOW_MANY_ROOMMATES,
    // @ts-expect-error
    wizardBodyTemplate: RoomHowManyRoommates,
    progressStepKey: PROGRESS_STEP.PROPERTY_INFO,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.ROOMMATE_DESCRIPTION,
    // @ts-expect-error
    wizardBodyTemplate: RoommateDescription,
    progressStepKey: PROGRESS_STEP.PROPERTY_INFO,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.RENTER_PREFERENCES,
    // @ts-expect-error
    wizardBodyTemplate: RenterPreferences,
    progressStepKey: PROGRESS_STEP.PROPERTY_INFO,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.ROOM_CURRENT_PETS,
    // @ts-expect-error
    wizardBodyTemplate: RoomCurrentPets,
    progressStepKey: PROGRESS_STEP.PROPERTY_INFO,
    hideProgressStepper: false,
  },
];

const configStepGroupListingDetails: Array<WizardStepType> = [
  {
    stepKey: STEP.MONTHLY_RENT_AND_SECURITY_DEPOSIT,
    // @ts-expect-error
    wizardBodyTemplate: MonthlyRentAndSecurityDeposit,
    progressStepKey: PROGRESS_STEP.RENT_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.ROOM_UTILITIES_AMOUNT,
    // @ts-expect-error
    wizardBodyTemplate: RoomUtilitiesAmount,
    progressStepKey: PROGRESS_STEP.RENT_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.WHEN_IS_PROPERTY_AVAILABLE,
    // @ts-expect-error
    wizardBodyTemplate: WhenIsPropertyAvailable,
    progressStepKey: PROGRESS_STEP.RENT_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.PET_POLICY,
    // @ts-expect-error
    wizardBodyTemplate: PetPolicy,
    progressStepKey: PROGRESS_STEP.RENT_DETAILS,
    hideProgressStepper: false,
  },
];

const configStepGroupMedia = [
  {
    stepKey: STEP.ADD_PHOTOS,
    wizardBodyTemplate: AddPhotos,
    progressStepKey: PROGRESS_STEP.MEDIA,
    hideProgressStepper: false,
  },
];

const configStepGroupAmenities: Array<WizardStepType> = [
  {
    stepKey: STEP.AMENITIES_INTERIOR,
    // @ts-expect-error
    wizardBodyTemplate: AmenitiesInterior,
    progressStepKey: PROGRESS_STEP.AMENITIES,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.AMENITIES_PROPERTY,
    // @ts-expect-error
    wizardBodyTemplate: AmenitiesProperty,
    progressStepKey: PROGRESS_STEP.AMENITIES,
    hideProgressStepper: false,
  },
];

const configStepGroupScreeningCriteria: Array<WizardStepType> = [
  {
    stepKey: STEP.FINANCIAL_EXPECTATIONS,
    // @ts-expect-error
    wizardBodyTemplate: FinancialExpectations,
    progressStepKey: PROGRESS_STEP.SCREENING_CRITERIA,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.PET_POLICY_AND_PET_FEE,
    // @ts-expect-error
    wizardBodyTemplate: PetPolicyAndPetFee,
    progressStepKey: PROGRESS_STEP.SCREENING_CRITERIA,
    hideProgressStepper: false,
  },
];

const configStepGroupCostsAndFees: Array<WizardStepType> = [
  {
    stepKey: STEP.COSTS_AND_FEES,
    // @ts-expect-error
    wizardBodyTemplate: CostsAndFees,
    progressStepKey: PROGRESS_STEP.COSTS_AND_FEES,
    hideProgressStepper: false,
  },
];

const configStepGroupFinalDetails: Array<WizardStepType> = [
  {
    stepKey: STEP.LEASE_DURATION,
    // @ts-expect-error
    wizardBodyTemplate: LeaseDuration,
    progressStepKey: PROGRESS_STEP.FINAL_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.LEASE_DESCRIPTION,
    // @ts-expect-error
    wizardBodyTemplate: LeaseTerms,
    progressStepKey: PROGRESS_STEP.FINAL_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.WHO_IS_LISTING_PROPERTY,
    // @ts-expect-error
    wizardBodyTemplate: WhoIsListingProperty,
    progressStepKey: PROGRESS_STEP.FINAL_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.PHONE_NUMBER,
    // @ts-expect-error
    wizardBodyTemplate: PhoneNumber,
    progressStepKey: PROGRESS_STEP.FINAL_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.ALLOW_CONTACT_BY_PHONE,
    // @ts-expect-error
    wizardBodyTemplate: AllowContactByPhone,
    progressStepKey: PROGRESS_STEP.FINAL_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.TOUR_SCHEDULING_BOOKING_METHOD,
    // @ts-expect-error - Legacy component interface mismatch
    wizardBodyTemplate: TourSchedulingBookingMethod,
    progressStepKey: PROGRESS_STEP.FINAL_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.TOUR_SCHEDULING_TIMEZONE,
    // @ts-expect-error - Legacy component interface mismatch
    wizardBodyTemplate: TourSchedulingTimeZone,
    progressStepKey: PROGRESS_STEP.FINAL_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.TOUR_SCHEDULING_AVAILABILITY,
    // @ts-expect-error - Legacy component interface mismatch
    wizardBodyTemplate: TourSchedulingAvailability,
    progressStepKey: PROGRESS_STEP.FINAL_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.DESCRIBE_PROPERTY,
    // @ts-expect-error
    wizardBodyTemplate: DescribeProperty,
    progressStepKey: PROGRESS_STEP.FINAL_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.ACCEPT_ZILLOW_APPLICATIONS,
    // @ts-expect-error
    wizardBodyTemplate: AcceptZillowApplications,
    progressStepKey: PROGRESS_STEP.FINAL_DETAILS,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.FAST_POST_REVIEW,
    // @ts-expect-error
    wizardBodyTemplate: FastPostReview,
    progressStepKey: PROGRESS_STEP.FINAL_DETAILS,
    hideProgressStepper: false,
  },
];

const configStepGroupReview: Array<WizardStepType> = [
  {
    stepKey: STEP.REVIEW,
    // @ts-expect-error
    wizardBodyTemplate: Review,
    progressStepKey: PROGRESS_STEP.REVIEW,
    hideProgressStepper: false,
  },
];

const configStepGroupTourBooking: Array<WizardStepType> = [
  {
    stepKey: STEP.TOUR_SCHEDULING_BOOKING_METHOD_RELIST,
    // @ts-expect-error - Legacy component interface mismatch
    wizardBodyTemplate: TourSchedulingBookingMethod,
    progressStepKey: PROGRESS_STEP.TOUR_BOOKING,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.TOUR_SCHEDULING_TIMEZONE_RELIST,
    // @ts-expect-error - Legacy component interface mismatch
    wizardBodyTemplate: TourSchedulingTimeZone,
    progressStepKey: PROGRESS_STEP.TOUR_BOOKING,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.TOUR_SCHEDULING_AVAILABILITY_RELIST,
    // @ts-expect-error - Legacy component interface mismatch
    wizardBodyTemplate: TourSchedulingAvailability,
    progressStepKey: PROGRESS_STEP.TOUR_BOOKING,
    hideProgressStepper: false,
  },
];

const configStepGroupPayAndPublish: Array<WizardStepType> = [
  {
    stepKey: STEP.PAY_AND_PUBLISH,
    // @ts-expect-error
    wizardBodyTemplate: PayAndPublish,
    progressStepKey: PROGRESS_STEP.PAY_AND_PUBLISH,
    hideProgressStepper: false,
  },
];

const configStepGroupRelist: Array<WizardStepType> = [
  {
    stepKey: STEP.UPDATE_RECENTLY_CHANGED,
    // @ts-expect-error
    wizardBodyTemplate: RelistConfirmDetails,
    progressStepKey: PROGRESS_STEP.UPDATE,
    hideProgressStepper: false,
  },
  {
    stepKey: STEP.CONSIDER_ADDITIONAL_DETAILS,
    // @ts-expect-error
    wizardBodyTemplate: RelistAdditionalDetails,
    progressStepKey: PROGRESS_STEP.UPDATE,
    hideProgressStepper: false,
  },
];

// @ts-expect-error
const config: Array<WizardStepType> = [
  ...configStepGroupPropertyInfo,
  ...configStepGroupListingDetails,
  ...configStepGroupMedia,
  ...configStepGroupAmenities,
  ...configStepGroupScreeningCriteria,
  ...configStepGroupCostsAndFees,
  ...configStepGroupFinalDetails,
  ...configStepGroupRelist,
  ...configStepGroupTourBooking,
  ...configStepGroupReview,
  ...configStepGroupPayAndPublish,
];

/** multiplex listing config */
const configMultiplexStepGroupPropertyInfo: Array<WizardStepType> = [
  {
    stepKey: STEP.MULTIPLEX_PROPERTY_DETAILS,
    // @ts-expect-error
    wizardBodyTemplate: MultiplexPropertyDetails,
    progressStepKey: PROGRESS_STEP.PROPERTY_DETAILS,
    hideProgressStepper: false,
  },
];

const configMultiplexStepGroupOverview: Array<WizardStepType> = [
  {
    stepKey: STEP.MULTIPLEX_OVERVIEW,
    // @ts-expect-error
    wizardBodyTemplate: MultiplexOverview,
    progressStepKey: PROGRESS_STEP.MULTIPLEX_OVERVIEW,
    hideProgressStepper: false,
  },
];

const configMultiplexStepGroupListingPublished: Array<WizardStepType> = [
  {
    stepKey: STEP.MULTIPLEX_LISTING_PUBLISHED,
    wizardBodyTemplate: MultiplexListingPublished,
    progressStepKey: PROGRESS_STEP.PUBLISHED,
    hideProgressStepper: false,
  },
];

const configMultiplex: Array<WizardStepType> = [
  ...configMultiplexStepGroupPropertyInfo,
  ...configMultiplexStepGroupOverview,
  // reuse from regular config
  ...configStepGroupReview,
  ...configStepGroupPayAndPublish,
  ...configMultiplexStepGroupListingPublished,
];

const STEP_TO_STEP_GROUP_MAP = getStepToGroupStepMap(config, PROGRESS_STEP_CONFIG);
const STEP_TO_STEP_RELISTING_GROUP_MAP = getStepToGroupStepMap(config, PROGRESS_STEP_RELISTING_CONFIG);
const STEP_TO_STEP_RELISTING_TOUR_BOOKING_GROUP_MAP = getStepToGroupStepMap(
  config,
  PROGRESS_STEP_RELISTING_TOUR_BOOKING_CONFIG,
);
const STEP_TO_STEP_MULTIPLEX_LISTING_GROUP_MAP = getStepToGroupStepMap(
  configMultiplex,
  PROGRESS_STEP_MULTIPLEX_LISTING_CONFIG,
);
const SUB_STEP_MAP = getSubStepMap(config);

const ORIGINAL_CONFIG = [...config];

export {
  config,
  configMultiplex,
  configStepGroupRelist,
  configStepGroupReview,
  configStepGroupTourBooking,
  ORIGINAL_CONFIG,
  SUB_STEP_MAP,
  STEP_TO_STEP_GROUP_MAP,
  STEP_TO_STEP_RELISTING_GROUP_MAP,
  STEP_TO_STEP_RELISTING_TOUR_BOOKING_GROUP_MAP,
  STEP_TO_STEP_MULTIPLEX_LISTING_GROUP_MAP,
};
